import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';

export default function EquipmentFinancingPage() {
  useEffect(() => {
    track('page_view', { page: 'equipment_financing' });
  }, []);

  useEffect(() => {
    document.title = 'Equipment Financing Guide | Business Equipment Loans & Leasing Options';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn about equipment financing options for your business. Understand equipment loans, leasing, qualification requirements, and how to finance machinery, vehicles, technology, and more.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'equipment financing, equipment loans, equipment leasing, business equipment, machinery financing, vehicle financing, technology financing');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);

    // Add structured data for FAQs
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is equipment financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Equipment financing is a type of business funding specifically designed to help companies purchase or lease equipment needed for operations. The equipment itself serves as collateral, which often makes it easier to qualify for compared to unsecured loans. This can include machinery, vehicles, technology, medical equipment, restaurant equipment, construction equipment, and more."
          }
        },
        {
          "@type": "Question",
          "name": "What's the difference between equipment loans and equipment leasing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Equipment Loans: You borrow money to purchase equipment and own it outright once the loan is paid off. You can depreciate the asset, and there may be tax benefits. Equipment Leasing: You rent the equipment for a set period with monthly payments. At the end of the lease, you may have options to purchase, return, or upgrade. Leasing often has lower monthly payments and allows for easier equipment upgrades."
          }
        },
        {
          "@type": "Question",
          "name": "How much can I finance for equipment?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Equipment financing typically ranges from $25,000 to $1,000,000+, depending on the equipment value, your business qualifications, and lender requirements. Some lenders may finance up to 100% of the equipment cost, while others require a down payment of 10-20%. The amount you qualify for depends on your business revenue, credit score, time in business, and the equipment's value."
          }
        },
        {
          "@type": "Question",
          "name": "What credit score do I need for equipment financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most equipment financing lenders prefer a credit score of 600 or higher, though requirements vary. Higher credit scores (680+) typically qualify for better rates and terms. Some specialized equipment lenders may work with lower credit scores if you have strong revenue and the equipment has good resale value. The equipment serving as collateral makes this type of financing more accessible than unsecured loans."
          }
        },
        {
          "@type": "Question",
          "name": "How long does equipment financing approval take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Equipment financing approval is typically faster than traditional business loans. Many lenders can provide pre-approval within 24-48 hours. Full approval and funding usually takes 3-7 business days, depending on the equipment value, documentation requirements, and lender processes. Larger equipment purchases or more complex transactions may take 1-2 weeks."
          }
        },
        {
          "@type": "Question",
          "name": "What types of equipment can be financed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most business equipment can be financed, including: Manufacturing machinery and tools, Commercial vehicles and trucks, Restaurant and food service equipment, Medical and dental equipment, Construction equipment, Technology and computers, Office furniture and fixtures, Agricultural equipment, Printing and packaging equipment, Fitness and gym equipment. The equipment must typically be used for business purposes and have a useful life that extends beyond the loan term."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a down payment for equipment financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Down payment requirements vary by lender and your qualifications. Some lenders offer 100% financing with no down payment, especially for well-qualified borrowers or newer equipment. Others may require 10-20% down. Factors affecting down payment requirements include your credit score, time in business, equipment type and age, and the lender's policies. A larger down payment may help you secure better rates."
          }
        },
        {
          "@type": "Question",
          "name": "Can I finance used equipment?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, many lenders finance used equipment, though terms may differ from new equipment financing. Used equipment typically requires a larger down payment (15-25%) and may have shorter loan terms. The equipment's age, condition, and resale value are important factors. Some lenders have age limits (e.g., equipment must be less than 10 years old). Used equipment may have slightly higher interest rates than new equipment."
          }
        },
        {
          "@type": "Question",
          "name": "What are typical interest rates for equipment financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Equipment financing rates typically range from 5% to 30% APR, depending on several factors: your credit score and business financials, equipment type and age (new vs. used), loan amount and term length, down payment amount, and lender type (bank, online lender, manufacturer financing). Well-qualified borrowers with strong credit and established businesses can often secure rates in the 5-12% range. Rates are generally lower than unsecured business loans because the equipment serves as collateral."
          }
        },
        {
          "@type": "Question",
          "name": "What happens if I can't make payments on equipment financing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "If you default on equipment financing, the lender can repossess the equipment since it serves as collateral. This process typically involves: Initial late payment fees and penalties, communication from the lender about bringing the account current, potential repossession of the equipment if payments aren't made, negative impact on your business credit score, and possible legal action for any remaining balance after equipment sale. It's important to communicate with your lender early if you're experiencing financial difficulties, as they may offer solutions like payment restructuring or temporary forbearance."
          }
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(faqSchema);
  }, []);

  const equipmentTypes = [
    {
      category: 'Manufacturing & Industrial',
      icon: 'ri-settings-3-line',
      examples: ['CNC machines', 'Assembly line equipment', 'Industrial tools', 'Packaging machinery'],
      typical: '$50K - $500K'
    },
    {
      category: 'Transportation & Vehicles',
      icon: 'ri-truck-line',
      examples: ['Commercial trucks', 'Delivery vans', 'Fleet vehicles', 'Specialized transport'],
      typical: '$30K - $200K'
    },
    {
      category: 'Technology & IT',
      icon: 'ri-computer-line',
      examples: ['Servers and hardware', 'POS systems', 'Software infrastructure', 'Telecommunications'],
      typical: '$25K - $150K'
    },
    {
      category: 'Medical & Healthcare',
      icon: 'ri-heart-pulse-line',
      examples: ['Diagnostic equipment', 'Dental chairs', 'Imaging machines', 'Lab equipment'],
      typical: '$50K - $1M+'
    },
    {
      category: 'Restaurant & Food Service',
      icon: 'ri-restaurant-line',
      examples: ['Commercial ovens', 'Refrigeration units', 'Food prep equipment', 'POS systems'],
      typical: '$25K - $200K'
    },
    {
      category: 'Construction',
      icon: 'ri-hammer-line',
      examples: ['Excavators', 'Bulldozers', 'Cranes', 'Power tools'],
      typical: '$50K - $500K'
    }
  ];

  const loanVsLease = [
    {
      aspect: 'Ownership',
      loan: 'You own the equipment immediately',
      lease: 'Lessor owns; you may purchase at end'
    },
    {
      aspect: 'Monthly Payment',
      loan: 'Typically higher payments',
      lease: 'Usually lower monthly payments'
    },
    {
      aspect: 'Tax Benefits',
      loan: 'Depreciation deductions, Section 179',
      lease: 'Lease payments may be deductible'
    },
    {
      aspect: 'Flexibility',
      loan: 'Equipment is yours to keep or sell',
      lease: 'Easy to upgrade at lease end'
    },
    {
      aspect: 'Down Payment',
      loan: '0-20% typically required',
      lease: 'Often minimal or no down payment'
    },
    {
      aspect: 'Best For',
      loan: 'Long-term use, asset building',
      lease: 'Frequently updated equipment'
    }
  ];

  const qualificationRequirements = [
    {
      requirement: 'Time in Business',
      typical: '6+ months',
      ideal: '2+ years',
      notes: 'Newer businesses may qualify with strong credit and down payment'
    },
    {
      requirement: 'Credit Score',
      typical: '600+',
      ideal: '680+',
      notes: 'Equipment as collateral makes this more flexible than unsecured loans'
    },
    {
      requirement: 'Annual Revenue',
      typical: '$100K+',
      ideal: '$250K+',
      notes: 'Must demonstrate ability to make monthly payments'
    },
    {
      requirement: 'Down Payment',
      typical: '0-20%',
      ideal: '10-15%',
      notes: 'Varies by lender, equipment type, and borrower qualifications'
    },
    {
      requirement: 'Equipment Value',
      typical: '$25K minimum',
      ideal: 'No maximum',
      notes: 'Equipment must have resale value and useful life beyond loan term'
    }
  ];

  const financingProcess = [
    {
      step: 1,
      title: 'Identify Equipment Needs',
      description: 'Determine what equipment your business needs and get quotes from vendors.',
      timeline: '1-3 days',
      tips: ['Get multiple vendor quotes', 'Consider new vs. used', 'Factor in installation costs', 'Research equipment specifications']
    },
    {
      step: 2,
      title: 'Pre-Qualification',
      description: 'Submit basic business information to see what financing options you may qualify for.',
      timeline: '24-48 hours',
      tips: ['Prepare financial documents', 'Check your credit score', 'Have equipment quotes ready', 'Compare multiple lenders']
    },
    {
      step: 3,
      title: 'Formal Application',
      description: 'Complete full application with detailed business and equipment information.',
      timeline: '1-2 days',
      tips: ['Provide accurate information', 'Include equipment specifications', 'Submit all requested documents', 'Be responsive to lender questions']
    },
    {
      step: 4,
      title: 'Underwriting & Approval',
      description: 'Lender reviews your application, verifies information, and evaluates equipment value.',
      timeline: '2-5 days',
      tips: ['Respond quickly to requests', 'Provide additional documentation promptly', 'Be available for questions', 'Review terms carefully']
    },
    {
      step: 5,
      title: 'Documentation & Funding',
      description: 'Sign loan documents and receive funding to purchase equipment.',
      timeline: '1-3 days',
      tips: ['Review all terms before signing', 'Understand payment schedule', 'Coordinate with equipment vendor', 'Plan for delivery and installation']
    }
  ];

  const useCases = [
    {
      title: 'Manufacturing Expansion',
      scenario: 'A metal fabrication shop needs new CNC machines to increase production capacity and take on larger contracts.',
      equipment: 'Two CNC machines',
      cost: '$180,000',
      solution: 'Equipment loan with 15% down payment, 5-year term at 8.5% APR',
      outcome: 'Increased production by 40%, secured three major contracts within 6 months'
    },
    {
      title: 'Restaurant Opening',
      scenario: 'A new restaurant needs complete kitchen equipment including ovens, refrigeration, and food prep stations.',
      equipment: 'Full commercial kitchen setup',
      cost: '$120,000',
      solution: 'Equipment financing with 10% down, 7-year term at 9.2% APR',
      outcome: 'Opened on schedule, equipment warranty covered by financing terms'
    },
    {
      title: 'Medical Practice Upgrade',
      scenario: 'A dental practice wants to add advanced imaging equipment to offer more comprehensive services.',
      equipment: 'Digital X-ray and 3D imaging system',
      cost: '$95,000',
      solution: 'Equipment lease with option to purchase, 5-year term, $1,800/month',
      outcome: 'Attracted new patients, increased revenue by $15K/month'
    },
    {
      title: 'Construction Fleet Addition',
      scenario: 'A growing construction company needs additional heavy equipment to handle multiple simultaneous projects.',
      equipment: 'Excavator and skid steer loader',
      cost: '$210,000',
      solution: 'Equipment loan with 20% down, 6-year term at 7.8% APR',
      outcome: 'Took on two additional projects, ROI achieved in 18 months'
    },
    {
      title: 'Technology Upgrade',
      scenario: 'An IT services company needs to upgrade server infrastructure to support growing client base.',
      equipment: 'Enterprise servers and networking equipment',
      cost: '$75,000',
      solution: 'Equipment lease with 3-year term, option to upgrade at end',
      outcome: 'Improved service capacity, retained all clients during growth phase'
    },
    {
      title: 'Delivery Fleet Expansion',
      scenario: 'A logistics company needs additional delivery vehicles to expand service area and meet demand.',
      equipment: 'Five commercial delivery vans',
      cost: '$165,000',
      solution: 'Equipment financing with 10% down, 5-year term at 8.9% APR',
      outcome: 'Expanded to three new territories, increased monthly revenue by $45K'
    }
  ];

  const faqs = [
    {
      question: 'What is equipment financing?',
      answer: 'Equipment financing is a type of business funding specifically designed to help companies purchase or lease equipment needed for operations. The equipment itself serves as collateral, which often makes it easier to qualify for compared to unsecured loans. This can include machinery, vehicles, technology, medical equipment, restaurant equipment, construction equipment, and more.'
    },
    {
      question: "What's the difference between equipment loans and equipment leasing?",
      answer: 'Equipment Loans: You borrow money to purchase equipment and own it outright once the loan is paid off. You can depreciate the asset, and there may be tax benefits. Equipment Leasing: You rent the equipment for a set period with monthly payments. At the end of the lease, you may have options to purchase, return, or upgrade. Leasing often has lower monthly payments and allows for easier equipment upgrades.'
    },
    {
      question: 'How much can I finance for equipment?',
      answer: "Equipment financing typically ranges from $25,000 to $1,000,000+, depending on the equipment value, your business qualifications, and lender requirements. Some lenders may finance up to 100% of the equipment cost, while others require a down payment of 10-20%. The amount you qualify for depends on your business revenue, credit score, time in business, and the equipment's value."
    },
    {
      question: 'What credit score do I need for equipment financing?',
      answer: 'Most equipment financing lenders prefer a credit score of 600 or higher, though requirements vary. Higher credit scores (680+) typically qualify for better rates and terms. Some specialized equipment lenders may work with lower credit scores if you have strong revenue and the equipment has good resale value. The equipment serving as collateral makes this type of financing more accessible than unsecured loans.'
    },
    {
      question: 'How long does equipment financing approval take?',
      answer: 'Equipment financing approval is typically faster than traditional business loans. Many lenders can provide pre-approval within 24-48 hours. Full approval and funding usually takes 3-7 business days, depending on the equipment value, documentation requirements, and lender processes. Larger equipment purchases or more complex transactions may take 1-2 weeks.'
    },
    {
      question: 'What types of equipment can be financed?',
      answer: 'Most business equipment can be financed, including: Manufacturing machinery and tools, Commercial vehicles and trucks, Restaurant and food service equipment, Medical and dental equipment, Construction equipment, Technology and computers, Office furniture and fixtures, Agricultural equipment, Printing and packaging equipment, Fitness and gym equipment. The equipment must typically be used for business purposes and have a useful life that extends beyond the loan term.'
    },
    {
      question: 'Do I need a down payment for equipment financing?',
      answer: "Down payment requirements vary by lender and your qualifications. Some lenders offer 100% financing with no down payment, especially for well-qualified borrowers or newer equipment. Others may require 10-20% down. Factors affecting down payment requirements include your credit score, time in business, equipment type and age, and the lender's policies. A larger down payment may help you secure better rates."
    },
    {
      question: 'Can I finance used equipment?',
      answer: "Yes, many lenders finance used equipment, though terms may differ from new equipment financing. Used equipment typically requires a larger down payment (15-25%) and may have shorter loan terms. The equipment's age, condition, and resale value are important factors. Some lenders have age limits (e.g., equipment must be less than 10 years old). Used equipment may have slightly higher interest rates than new equipment."
    },
    {
      question: 'What are typical interest rates for equipment financing?',
      answer: 'Equipment financing rates typically range from 5% to 30% APR, depending on several factors: your credit score and business financials, equipment type and age (new vs. used), loan amount and term length, down payment amount, and lender type (bank, online lender, manufacturer financing). Well-qualified borrowers with strong credit and established businesses can often secure rates in the 5-12% range. Rates are generally lower than unsecured business loans because the equipment serves as collateral.'
    },
    {
      question: "What happens if I can't make payments on equipment financing?",
      answer: "If you default on equipment financing, the lender can repossess the equipment since it serves as collateral. This process typically involves: Initial late payment fees and penalties, communication from the lender about bringing the account current, potential repossession of the equipment if payments aren't made, negative impact on your business credit score, and possible legal action for any remaining balance after equipment sale. It's important to communicate with your lender early if you're experiencing financial difficulties, as they may offer solutions like payment restructuring or temporary forbearance."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-gray-900 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/business-funding" className="hover:text-gray-900 transition-colors cursor-pointer">Funding Options</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">Equipment Financing</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-tools-line"></i>
                <span>Equipment Financing Guide</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Finance the Equipment Your Business Needs
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Learn how equipment financing and leasing can help you acquire machinery, vehicles, technology, and other essential business equipment without depleting your cash reserves.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_click', { location: 'equipment_hero', action: 'prequalify' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Check Your Options
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  Learn More
                </a>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Amount Range', value: '$25K-$1M+' },
                  { label: 'Approval Time', value: '3-7 days' },
                  { label: 'Typical Term', value: '1-7 years' },
                  { label: 'Min Credit', value: '600+' }
                ].map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="text-2xl font-bold mb-1">{stat.value}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://public.readdy.ai/ai/img_res/31a1e588ed10cd673afd81ad3ba3cdf0.jpg"
                alt="Business equipment and machinery"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Equipment Financing */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4">
              Understanding Equipment Financing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What is Equipment Financing?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Equipment financing is a specialized form of business funding that allows you to acquire necessary equipment through loans or leases, using the equipment itself as collateral.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-100">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl mb-6">
                <i className="ri-shopping-cart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Equipment Loans</h3>
              <p className="text-gray-700 mb-6">
                Borrow money to purchase equipment outright. You own the equipment from day one and can depreciate it for tax purposes. Once the loan is paid off, the equipment is yours free and clear.
              </p>
              <ul className="space-y-3">
                {[
                  'Immediate ownership',
                  'Build business assets',
                  'Tax depreciation benefits',
                  'Section 179 deductions',
                  'Freedom to sell or modify'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <i className="ri-check-line text-teal-600"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-100">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl mb-6">
                <i className="ri-refresh-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Equipment Leasing</h3>
              <p className="text-gray-700 mb-6">
                Rent equipment for a set period with monthly payments. At lease end, you can purchase the equipment, return it, or upgrade to newer models. Great for technology and equipment that becomes obsolete quickly.
              </p>
              <ul className="space-y-3">
                {[
                  'Lower monthly payments',
                  'Easy equipment upgrades',
                  'Preserve working capital',
                  'Potential tax deductions',
                  'Flexible end-of-lease options'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <i className="ri-check-line text-cyan-600"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Loan vs Lease Comparison */}
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Equipment Loan vs. Lease Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Aspect</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-teal-700">Equipment Loan</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-700">Equipment Lease</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loanVsLease.map((row, index) => (
                    <tr key={index} className="hover:bg-white transition-colors">
                      <td className="px-4 py-4 font-semibold text-gray-900">{row.aspect}</td>
                      <td className="px-4 py-4 text-gray-700">{row.loan}</td>
                      <td className="px-4 py-4 text-gray-700">{row.lease}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Equipment */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 text-sm font-semibold rounded-full mb-4">
              Equipment Categories
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Equipment Can Be Financed?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Almost any type of business equipment can be financed, from heavy machinery to technology systems
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipmentTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg mb-4">
                  <i className={`${type.icon} text-teal-600 text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{type.category}</h3>
                <ul className="space-y-2 mb-4">
                  {type.examples.map((example, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-line text-teal-500 text-xs"></i>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Typical Range:</span>
                  <p className="text-sm font-semibold text-gray-900">{type.typical}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualification Requirements */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              Qualification Criteria
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Equipment Financing Requirements
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Equipment financing is often easier to qualify for than unsecured loans because the equipment serves as collateral
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Requirement</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Typical Minimum</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ideal</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {qualificationRequirements.map((req, index) => (
                  <tr key={index} className="hover:bg-white transition-colors">
                    <td className="px-4 py-4 font-semibold text-gray-900">{req.requirement}</td>
                    <td className="px-4 py-4 text-gray-700">{req.typical}</td>
                    <td className="px-4 py-4 text-teal-600 font-semibold">{req.ideal}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{req.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-teal-50 rounded-xl p-6 border border-teal-100">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-lightbulb-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Why Equipment Financing is More Accessible</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Because the equipment itself serves as collateral, lenders face less risk compared to unsecured loans. This often means more flexible qualification requirements, especially for businesses with shorter operating histories or lower credit scores. The equipment's resale value provides security for the lender, making approval more likely.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Process */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4">
              Step-by-Step Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Equipment Financing Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From identifying your equipment needs to receiving funding—here's what to expect
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-200 via-cyan-300 to-blue-200 -translate-x-1/2"></div>

            <div className="space-y-12">
              {financingProcess.map((step, index) => (
                <div key={index} className={`relative grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:pl-12 lg:col-start-2'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg text-white font-bold">
                          {step.step}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-700 mb-4">{step.description}</p>
                      <div className="flex items-center gap-2 text-sm text-teal-600 font-semibold mb-4">
                        <i className="ri-time-line"></i>
                        <span>Timeline: {step.timeline}</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Tips:</h4>
                        <ul className="space-y-1">
                          {step.tips.map((tip, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <i className="ri-arrow-right-s-line text-teal-500 mt-0.5"></i>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="hidden lg:block absolute left-1/2 -translate-x-1/2">
                    <div className="w-4 h-4 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real-World Use Cases */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 text-sm font-semibold rounded-full mb-4">
              Real-World Examples
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Equipment Financing Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how businesses across different industries use equipment financing to grow and succeed
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:border-teal-300 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{useCase.scenario}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Equipment</span>
                    <span className="text-sm font-semibold text-gray-900">{useCase.equipment}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Total Cost</span>
                    <span className="text-sm font-semibold text-teal-600">{useCase.cost}</span>
                  </div>
                  <div className="py-2">
                    <span className="text-sm text-gray-600 block mb-1">Solution</span>
                    <span className="text-sm text-gray-900">{useCase.solution}</span>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
                  <div className="flex items-start gap-2">
                    <i className="ri-trophy-line text-teal-600 mt-0.5"></i>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Outcome</h4>
                      <p className="text-sm text-gray-700">{useCase.outcome}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pros and Cons */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
              Balanced Perspective
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advantages & Considerations
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding both the benefits and potential drawbacks helps you make an informed decision
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Advantages */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-teal-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-thumb-up-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Advantages</h3>
              </div>
              <ul className="space-y-4">
                {[
                  { title: 'Preserve Cash Flow', desc: 'Acquire equipment without large upfront costs, keeping working capital available for operations' },
                  { title: 'Equipment as Collateral', desc: 'Easier qualification compared to unsecured loans since equipment secures the financing' },
                  { title: 'Tax Benefits', desc: 'Potential deductions through depreciation (loans) or lease payments (leases)' },
                  { title: 'Fast Approval', desc: 'Typically faster than traditional business loans, often 3-7 days to funding' },
                  { title: 'Flexible Terms', desc: 'Terms can be matched to equipment lifespan, from 1-7 years or more' },
                  { title: 'Build Business Assets', desc: 'With loans, you build equity in equipment that becomes a business asset' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Considerations */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl">
                  <i className="ri-information-line text-gray-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Considerations</h3>
              </div>
              <ul className="space-y-4">
                {[
                  { title: 'Long-Term Commitment', desc: 'You\'re committed to payments for the full term, typically 1-7 years' },
                  { title: 'Equipment Depreciation', desc: 'Equipment may lose value faster than loan balance decreases, especially for technology' },
                  { title: 'Down Payment May Be Required', desc: 'Some lenders require 10-20% down, though 100% financing is sometimes available' },
                  { title: 'Equipment Restrictions', desc: 'Lenders may have requirements about equipment age, type, and condition' },
                  { title: 'Risk of Repossession', desc: 'If you default, the lender can repossess the equipment' },
                  { title: 'Total Cost Consideration', desc: 'Interest charges mean you\'ll pay more than the equipment\'s purchase price over time' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-gray-400 mt-1 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4">
              Common Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Equipment Financing FAQs
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to the most common questions about equipment financing
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-gray-50 rounded-xl border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex items-center justify-between cursor-pointer p-6 font-semibold text-gray-900">
                  <span className="pr-4">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-xl text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Finance Your Equipment?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            See if you pre-qualify for equipment financing. Quick check, no impact on your credit score.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/prequalification"
              onClick={() => track('cta_click', { location: 'equipment_bottom', action: 'prequalify' })}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Check Your Options
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/business-funding"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              View All Funding Types
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
